/**
 * The Throughput section's setup: a gear beside "Throughput History", opening
 * a window that refreshes the team's throughput and sets its range and cap
 * (shipcast-o7j.7).
 *
 * - **Refresh** fetches the dataset fresh and re-cuts this team's throughput
 *   only — the shared {@link TeamRefreshPanel}.
 * - **Range** — the last N weeks (rolling, ending on the fetch day) or fixed
 *   dates — and **cap** — no day counts above N items — are the team's own.
 *   **Apply** saves them and the server re-cuts the throughput from the
 *   stored history at once, without fetching (Q19).
 * - The charts show the whole stored history raw and preview the DRAFT:
 *   what falls outside the range, what the cap removes, and how the used
 *   distribution differs from all history — before anything is saved.
 */

import { useState } from 'react';
import { Button } from '@lean-agility/design-system/react';

import { Dialog } from '@/components/dialog';

import { useSetTeamThroughputSettings, useTeamThroughput, useTrackerCapabilities } from '../hooks';
import { cutThroughput, isSampleable, settingsProblem, MAX_ROLLING_WEEKS } from '../throughputCut';
import type { TeamThroughputResponse, ThroughputSettingsBody } from '../types';
import { formatTrackerWarning } from '../warnings';
import { SetupButton } from './SetupButton';
import { useSetupWindow } from './setupWindow';
import { ThroughputRawCharts } from './ThroughputRawCharts';
import { TeamRefreshPanel } from './TeamRefreshPanel';
import { useTeamRefresh } from './teamRefresh';

export interface ThroughputSetupProps {
  /** The team's server id; `undefined` until the team is first saved. */
  teamId: string | undefined;
  /** Reload the form's copy of the team once its throughput changed. */
  onApplied?: () => void;
}

export function ThroughputSetup({ teamId, onApplied }: ThroughputSetupProps) {
  const { data: capabilities } = useTrackerCapabilities();
  if (!capabilities?.licensed || !capabilities.configured || !teamId) return null;
  return <Setup teamId={teamId} onApplied={onApplied} />;
}

function Setup({ teamId, onApplied }: { teamId: string; onApplied?: () => void }) {
  // Outside React state: a landed refresh or an applied range reloads the
  // form, which remounts this section (see ./setupWindow).
  const [open, setOpen] = useSetupWindow(teamId, 'throughput');
  const refresh = useTeamRefresh(teamId, 'throughput', onApplied);

  return (
    <>
      <SetupButton refresh={refresh} label="Throughput setup" onClick={() => setOpen(true)} />
      {open && (
        <Dialog title="Throughput setup" onClose={() => setOpen(false)} maxWidth="max-w-2xl">
          <div className="space-y-5">
            <TeamRefreshPanel refresh={refresh} />
            {/* Range and cap cut a dataset's history; a hand-typed series
                has none, and is never capped (Q8). */}
            {refresh.datasetName !== null && <ThroughputSettings teamId={teamId} onApplied={onApplied} />}
          </div>
        </Dialog>
      )}
    </>
  );
}

function ThroughputSettings({ teamId, onApplied }: { teamId: string; onApplied?: () => void }) {
  const { data: throughput, error } = useTeamThroughput(teamId);
  // Held here, not in the form: the form resets to the saved settings after
  // a save, and a warning the save returned must survive that.
  const save = useSetTeamThroughputSettings();

  if (error instanceof Error) {
    return (
      <p role="alert" className="text-sm text-feedback-error">
        Could not load the throughput settings: {error.message}
      </p>
    );
  }
  if (!throughput) return <p className="text-sm text-text-muted">Loading the throughput settings…</p>;

  const apply = (settings: ThroughputSettingsBody) =>
    save.mutate(
      { simulationTeamId: teamId, settings },
      {
        onSuccess: (saved) => {
          // An empty range leaves the team's throughput as it was: nothing to
          // reload, and the warning below says why.
          if (!saved.warnings.some((w) => w.kind === 'empty_throughput')) onApplied?.();
        },
      },
    );

  return (
    <>
      <SettingsForm
        key={JSON.stringify(throughput.settings)}
        throughput={throughput}
        saving={save.isPending}
        onApply={apply}
      />
      {save.error instanceof Error && (
        <p role="alert" className="text-sm text-feedback-error">
          Could not apply the settings: {save.error.message}
        </p>
      )}
      {save.data?.warnings.map((w, i) => (
        <p key={`${w.kind}-${i}`} role="alert" className="text-sm text-feedback-warning">
          {w.kind === 'empty_throughput'
            ? "The range holds no delivered day, so the team's throughput was left as it was. Widen the range."
            : formatTrackerWarning(w)}
        </p>
      ))}
    </>
  );
}

interface Draft {
  mode: 'rolling' | 'fixed';
  weeks: string;
  from: string;
  to: string;
  capOn: boolean;
  cap: string;
}

function draftOf(throughput: TeamThroughputResponse): Draft {
  const { range, cap } = throughput.settings;
  return {
    mode: range.mode,
    weeks: range.mode === 'rolling' ? String(range.weeks) : '12',
    // A fixed range starts from the current one: the likeliest edit is a nudge.
    from: range.mode === 'fixed' ? range.from : (throughput.history?.range_from ?? ''),
    to: range.mode === 'fixed' ? range.to : (throughput.history?.range_to ?? ''),
    capOn: cap != null,
    cap: cap != null ? String(cap) : '',
  };
}

function settingsOf(draft: Draft): ThroughputSettingsBody {
  return {
    range:
      draft.mode === 'rolling'
        ? { mode: 'rolling', weeks: Number(draft.weeks) }
        : { mode: 'fixed', from: draft.from, to: draft.to },
    cap: draft.capOn ? Number(draft.cap) : null,
  };
}

function SettingsForm({
  throughput,
  saving,
  onApply,
}: {
  throughput: TeamThroughputResponse;
  saving: boolean;
  onApply: (settings: ThroughputSettingsBody) => void;
}) {
  const [draft, setDraft] = useState(() => draftOf(throughput));
  const edit = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const settings = settingsOf(draft);
  const problem = settingsProblem(settings);
  const saved = settingsOf(draftOf(throughput));
  const changed = JSON.stringify(settings) !== JSON.stringify(saved);

  const history = throughput.history ?? null;
  // Preview the draft when it is valid; otherwise keep showing what is saved.
  // One pass over the stored days — cheap enough to redo on each keystroke.
  const cut = history ? cutThroughput(history.days, problem ? saved : settings, history.fetched_at.slice(0, 10)) : null;

  return (
    <div className="space-y-4">
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-text-primary">Range</legend>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="radio"
            name="throughput-range"
            checked={draft.mode === 'rolling'}
            onChange={() => edit({ mode: 'rolling' })}
          />
          The last
          <input
            type="number"
            aria-label="Weeks"
            min={1}
            max={MAX_ROLLING_WEEKS}
            value={draft.weeks}
            disabled={draft.mode !== 'rolling'}
            onChange={(e) => edit({ weeks: e.target.value })}
            className="
              w-16 rounded-sm border border-border-default bg-transparent px-1.5 py-0.5 text-text-primary
              disabled:opacity-60
            "
          />
          weeks, up to the last refresh
        </label>
        <label className="flex flex-wrap items-center gap-2 text-sm text-text-secondary">
          <input
            type="radio"
            name="throughput-range"
            checked={draft.mode === 'fixed'}
            onChange={() => edit({ mode: 'fixed' })}
          />
          From
          <input
            type="date"
            aria-label="First day"
            value={draft.from}
            disabled={draft.mode !== 'fixed'}
            onChange={(e) => edit({ from: e.target.value })}
            className="
              rounded-sm border border-border-default bg-transparent px-1.5 py-0.5 text-text-primary
              disabled:opacity-60
            "
          />
          to
          <input
            type="date"
            aria-label="Last day"
            value={draft.to}
            disabled={draft.mode !== 'fixed'}
            onChange={(e) => edit({ to: e.target.value })}
            className="
              rounded-sm border border-border-default bg-transparent px-1.5 py-0.5 text-text-primary
              disabled:opacity-60
            "
          />
        </label>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-text-primary">Cap</legend>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={draft.capOn}
            onChange={(e) => edit({ capOn: e.target.checked, cap: draft.cap || '10' })}
          />
          Count no day above
          <input
            type="number"
            aria-label="Cap, items per day"
            min={1}
            value={draft.cap}
            disabled={!draft.capOn}
            onChange={(e) => edit({ cap: e.target.value })}
            className="
              w-16 rounded-sm border border-border-default bg-transparent px-1.5 py-0.5 text-text-primary
              disabled:opacity-60
            "
          />
          items — for a backlog clean-up that closed many at once
        </label>
      </fieldset>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" size="sm" onClick={() => onApply(settings)} disabled={!changed || !!problem || saving}>
          {saving ? 'Applying…' : 'Apply'}
        </Button>
        {problem ? (
          <p role="alert" className="text-sm text-feedback-error">
            {problem}
          </p>
        ) : (
          cut && (
            <p className="text-sm text-text-secondary">
              {changed ? 'Would use' : 'Uses'} {cut.used.length} day{cut.used.length === 1 ? '' : 's'} from {cut.from}{' '}
              to {cut.to}
              {changed ? ' — Apply re-cuts the throughput now, without fetching.' : '.'}
              {changed && !isSampleable(cut.used) && (
                <span className="block text-feedback-warning">
                  No item was completed in this range, so Apply keeps the current throughput.
                </span>
              )}
            </p>
          )
        )}
      </div>

      {cut && cut.days.length > 0 ? (
        <ThroughputRawCharts cut={cut} cap={problem ? (saved.cap ?? null) : (settings.cap ?? null)} />
      ) : (
        <p className="text-sm text-text-muted">No history yet — Refresh fetches it from the dataset.</p>
      )}
    </div>
  );
}
